import { boundsOf, isBoxType, LINE_TYPES } from './markupGeometry'

export function styleOf(markup) {
  const s = markup.style || {}
  return {
    color: s.color || '#facc15',
    opacity: s.opacity == null ? 0.4 : s.opacity,
    strokeWidth: s.strokeWidth == null ? 2 : s.strokeWidth,
    fontSize: s.fontSize || 14,
  }
}

function arrowHead(x1, y1, x2, y2, size = 10) {
  const angle = Math.atan2(y2 - y1, x2 - x1)
  const a1 = angle - Math.PI / 6
  const a2 = angle + Math.PI / 6
  return [
    [x2, y2],
    [x2 - size * Math.cos(a1), y2 - size * Math.sin(a1)],
    [x2 - size * Math.cos(a2), y2 - size * Math.sin(a2)],
  ]
    .map((p) => p.join(','))
    .join(' ')
}

function Handle({ x, y, handle }) {
  return (
    <rect
      className="markup-handle"
      data-handle={handle}
      x={x - 4}
      y={y - 4}
      width={8}
      height={8}
    />
  )
}

export function MarkupShape({ markup, pageW, pageH, selected, showHandles }) {
  const g = markup.geometry || {}
  const { color, opacity, strokeWidth } = styleOf(markup)
  const x = (n) => (n || 0) * pageW
  const y = (n) => (n || 0) * pageH
  const hit = { stroke: 'transparent', strokeWidth: 14, fill: 'transparent' }

  let body = null
  if (markup.type === 'highlight') {
    body = (
      <>
        <rect x={x(g.x)} y={y(g.y)} width={(g.w || 0) * pageW} height={(g.h || 0) * pageH} fill="transparent" />
        <rect
          x={x(g.x)}
          y={y(g.y)}
          width={(g.w || 0) * pageW}
          height={(g.h || 0) * pageH}
          fill={color}
          fillOpacity={opacity}
        />
      </>
    )
  } else if (markup.type === 'rect') {
    body = (
      <>
        <rect x={x(g.x)} y={y(g.y)} width={(g.w || 0) * pageW} height={(g.h || 0) * pageH} fill="transparent" />
        <rect
          x={x(g.x)}
          y={y(g.y)}
          width={(g.w || 0) * pageW}
          height={(g.h || 0) * pageH}
          fill="none"
          stroke={color}
          strokeOpacity={opacity}
          strokeWidth={strokeWidth}
        />
      </>
    )
  } else if (markup.type === 'ellipse') {
    const cx = x(g.x) + ((g.w || 0) * pageW) / 2
    const cy = y(g.y) + ((g.h || 0) * pageH) / 2
    const rx = Math.abs((g.w || 0) * pageW) / 2
    const ry = Math.abs((g.h || 0) * pageH) / 2
    body = (
      <>
        <ellipse cx={cx} cy={cy} rx={rx} ry={ry} fill="transparent" />
        <ellipse
          cx={cx}
          cy={cy}
          rx={rx}
          ry={ry}
          fill="none"
          stroke={color}
          strokeOpacity={opacity}
          strokeWidth={strokeWidth}
        />
      </>
    )
  } else if (markup.type === 'line' || markup.type === 'arrow') {
    const x1 = x(g.x1)
    const y1 = y(g.y1)
    const x2 = x(g.x2)
    const y2 = y(g.y2)
    body = (
      <>
        <line x1={x1} y1={y1} x2={x2} y2={y2} {...hit} />
        <line
          x1={x1}
          y1={y1}
          x2={x2}
          y2={y2}
          stroke={color}
          strokeOpacity={opacity}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
        />
        {markup.type === 'arrow' ? (
          <polygon points={arrowHead(x1, y1, x2, y2)} fill={color} fillOpacity={opacity} />
        ) : null}
      </>
    )
  } else if (markup.type === 'ink') {
    const pts = Array.isArray(g.points) ? g.points : []
    if (pts.length >= 2) {
      const d = pts.map((p) => `${x(p.x)},${y(p.y)}`).join(' ')
      body = (
        <>
          <polyline points={d} fill="none" stroke="transparent" strokeWidth={14} />
          <polyline
            points={d}
            fill="none"
            stroke={color}
            strokeOpacity={opacity}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </>
      )
    }
  }

  if (!body) return null

  const b = boundsOf(markup)
  const bx = b.x * pageW
  const by = b.y * pageH
  const bw = b.w * pageW
  const bh = b.h * pageH

  return (
    <g className="markup-shape" data-markup-id={markup._id}>
      {body}
      {selected ? (
        <rect
          x={bx}
          y={by}
          width={bw}
          height={bh}
          fill="none"
          stroke="var(--ct-accent, #8092c4)"
          strokeDasharray="4 3"
          strokeWidth={1}
        />
      ) : null}
      {showHandles && isBoxType(markup.type) ? (
        <>
          <Handle x={bx} y={by} handle="nw" />
          <Handle x={bx + bw} y={by} handle="ne" />
          <Handle x={bx} y={by + bh} handle="sw" />
          <Handle x={bx + bw} y={by + bh} handle="se" />
        </>
      ) : null}
      {showHandles && LINE_TYPES.includes(markup.type) ? (
        <>
          <Handle x={x(g.x1)} y={y(g.y1)} handle="start" />
          <Handle x={x(g.x2)} y={y(g.y2)} handle="end" />
        </>
      ) : null}
    </g>
  )
}
